import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { budgetApi } from './api'
import type { BudgetItem, RecurringTransaction } from './types'

function useInvalidateBudget() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return () => {
    qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'budget') })
  }
}

export function useBudgetActive(month: number, year: number) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'active', month, year),
    queryFn: () => budgetApi.active(month, year),
  })
}

export function useBudgetAnalysis(month: number, year: number) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'analysis', month, year),
    queryFn: () => budgetApi.analysis(month, year),
  })
}

export function useAIRecommendations(month: number, year: number) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'ai-recommendations', month, year),
    queryFn: () => budgetApi.aiRecommendations(month, year),
    retry: false,
  })
}

export function useUpcomingPayments(days = 14) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'upcoming', days),
    queryFn: () => budgetApi.upcomingPayments(days),
  })
}

export function useBudgetCategories() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'categories'),
    queryFn: budgetApi.categories,
  })
}

export function useCategoryTransactions(category: string | null, month: number, year: number) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'transactions', category ?? '', month, year),
    queryFn: () => budgetApi.categoryTransactions(category as string, month, year),
    enabled: !!category,
  })
}

export function useRecurringTransactions() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'budget', 'recurring'),
    queryFn: budgetApi.recurring,
  })
}

export function useUpdateBudgetTotal() {
  const invalidate = useInvalidateBudget()
  return useMutation({
    mutationFn: (args: { budgetId: number; totalAmount: number }) =>
      budgetApi.updateTotal(args.budgetId, args.totalAmount),
    onSuccess: invalidate,
  })
}

export function useBulkSaveBudgetItems() {
  const invalidate = useInvalidateBudget()
  return useMutation({
    mutationFn: (args: {
      budgetId: number
      items: Array<Partial<BudgetItem> & { budgeted_amount: number }>
    }) => budgetApi.bulkSaveItems(args.budgetId, args.items),
    onSuccess: invalidate,
  })
}

export function useResetBudget() {
  const invalidate = useInvalidateBudget()
  return useMutation({
    mutationFn: (budgetId: number) => budgetApi.reset(budgetId),
    onSuccess: invalidate,
  })
}

export function useDeleteBudgetItem() {
  const invalidate = useInvalidateBudget()
  return useMutation({
    mutationFn: (itemId: number) => budgetApi.deleteItem(itemId),
    onSuccess: invalidate,
  })
}

export function useAddBudgetItem() {
  const invalidate = useInvalidateBudget()
  return useMutation({
    mutationFn: (args: { budgetId: number; item: Partial<BudgetItem> }) =>
      budgetApi.addItem(args.budgetId, args.item),
    onSuccess: invalidate,
  })
}

export function useGenerateAlerts() {
  return useMutation({
    mutationFn: (args: { month: number; year: number }) =>
      budgetApi.generateAlerts(args.month, args.year),
  })
}

export function useAddRecurring() {
  const invalidate = useInvalidateBudget()
  return useMutation({
    mutationFn: (body: Partial<RecurringTransaction>) => budgetApi.addRecurring(body),
    onSuccess: invalidate,
  })
}

export function useDeleteRecurring() {
  const invalidate = useInvalidateBudget()
  return useMutation({
    mutationFn: (id: number) => budgetApi.deleteRecurring(id),
    onSuccess: invalidate,
  })
}

export function useDetectRecurring() {
  return useMutation({
    mutationFn: () => budgetApi.detectRecurring(),
  })
}

export function useRecurringInsights() {
  return useMutation({
    mutationFn: (items: RecurringTransaction[]) => budgetApi.recurringInsights(items),
  })
}
